'use client';

import { useMemo } from 'react';
import AutoScroll from 'embla-carousel-auto-scroll';

import { GroqImage } from '@/helpers/Image';
import { IShortAdsMentor } from '@/sanity/pages/PageShortAdsTemplate';
import { Carousel, CarouselContent, CarouselItem } from '@/shadcn/ui/carousel';

// Mentor row (Figma node 3019:559) — the row is wider than the artboard and
// bleeds past both edges, so it runs as an infinite auto-scroll marquee. Slides
// are duplicated so the loop never shows a gap.
function MentorCard({ mentor }: { mentor: IShortAdsMentor }) {
  return (
    <div className="flex flex-col w-[39.5rem] lg:w-[48.75rem] gap-[3rem]">
      <div className="overflow-hidden relative w-full rounded-[1.5rem] aspect-[390/480] bg-white/[0.04]">
        {mentor.image && (
          <GroqImage
            src={mentor.image}
            alt={mentor.name ?? ''}
            className="!object-cover !object-bottom !w-full !h-full"
          />
        )}
      </div>

      <div className="flex flex-col gap-[1.5rem]">
        <p className="mb-0 leading-none text-white font-news text-[3rem] tracking-[-0.055rem]">
          {mentor.name}
        </p>
        {mentor.role && (
          <p className="mb-0 leading-none font-geist text-white/50 text-[2rem] tracking-[0.03rem]">
            {mentor.role}
          </p>
        )}
      </div>
    </div>
  );
}

export default function MentorsRowSection({
  mentors,
}: {
  mentors?: IShortAdsMentor[];
}) {
  const items = mentors ?? [];
  const autoScroll = useMemo(
    () => AutoScroll({ speed: 1, startDelay: 0, stopOnInteraction: false }),
    [],
  );
  const slides = items.length > 0 ? [...items, ...items] : [];

  if (items.length === 0) return null;

  return (
    <section className="overflow-hidden w-full">
      <Carousel
        opts={{ align: 'start', loop: true }}
        plugins={[autoScroll]}
        className="w-full">
        <CarouselContent className="ml-0">
          {slides.map((mentor, i) => (
            <CarouselItem key={i} className="basis-auto !pl-[2.5rem] lg:!pl-[2rem]">
              <MentorCard mentor={mentor} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </section>
  );
}
